import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Spinner } from 'reactstrap';
import { BiCheck, BiArrowBack } from 'react-icons/bi';
import { useClerk } from '@clerk/clerk-react';
import { useQuery } from '@tanstack/react-query';
import { createCheckoutSession, createPortalSession, fetchTrialEligibility } from '../api/billingApi';
import useBillingStatus from '../hooks/useBillingStatus';
import logoSrc from '../assets/marginpal_logo.svg';

const PLANS = [
    {
        tier: 'solo',
        name: 'Solo',
        price: 12,
        receipts: 75,
        color: '#10b981',
        features: [
            'Up to 3 members',
            'Manual quote management',
            'AI receipt scanning',
            'Quoted vs actual tracking',
            '75 receipt scans/mo',
        ],
    },
    {
        tier: 'pro',
        name: 'Pro',
        price: 29,
        receipts: 250,
        color: '#26ada9',
        features: [
            'Up to 10 members',
            'Branded quote PDFs',
            'Send quotes by email',
            'Customer accept/decline',
            'AI receipt scanning',
            'Quoted vs actual tracking',
            'Accounting integrations (Xero, QuickBooks, Sage)',

            '250 receipt scans/mo',
        ],
    },
    {
        tier: 'business',
        name: 'Business',
        price: 59,
        receipts: 600,
        popular: true,
        color: '#8b5cf6',
        features: [
            'Up to 20 members',
            'Branded quote PDFs',
            'Send quotes by email',
            'Customer accept/decline',
            'AI receipt scanning',
            'Quoted vs actual tracking',
            'AI margin insights',
            'Accounting integrations (Xero, QuickBooks, Sage)',

            '600 receipt scans/mo',
            'Priority support',
        ],
    },
];

export default function SubscribePage() {
    const [loading, setLoading] = useState(null);
    const [searchParams] = useSearchParams();
    const cancelled = searchParams.get('checkout') === 'cancelled';
    const { data: billingStatus } = useBillingStatus();

    // null while loading; default to "eligible" on lookup failure so a flaky
    // request doesn't strip the trial CTA from genuine first-time signups.
    // The server enforces the actual check at CreateCheckoutSession time.
    const { data: trialData } = useQuery({
        queryKey: ['trialEligibility'],
        queryFn: fetchTrialEligibility,
        staleTime: 60 * 1000,
    });
    const trialEligible = trialData?.eligible ?? true;

    const isPastDue = billingStatus?.status === 'past_due';

    const handleSubscribe = async (tier) => {
        setLoading(tier);
        try {
            const { url } = await createCheckoutSession(tier);
            window.location.href = url;
        } catch {
            setLoading(null);
        }
    };

    const handleManageBilling = async () => {
        setLoading('portal');
        try {
            const { url } = await createPortalSession();
            window.location.href = url;
        } catch {
            setLoading(null);
        }
    };

    const { signOut } = useClerk();

    return (
        <div style={{ minHeight: '100vh', background: '#f3f4f6', padding: '48px 24px' }}>
            <div className="mb-4" style={{ maxWidth: 960, margin: '0 auto' }}>
                <button
                    className="btn btn-link text-muted text-decoration-none p-0 d-flex align-items-center gap-1 small"
                    onClick={() => signOut()}
                >
                    <BiArrowBack size={16} />
                    Back to Login
                </button>
            </div>
            <div className="text-center mb-5">
                <img src={logoSrc} alt="MarginPal" style={{ width: 56, height: 56, marginBottom: 16 }} />
                <h1 className="fw-bold mb-2">Choose your plan</h1>
                <p className="text-muted mb-0">
                    {isPastDue
                        ? 'Your payment failed. Please update your payment method or choose a new plan.'
                        : 'Start managing your margins today.'}
                </p>
                {cancelled && (
                    <div className="text-warning mt-2 small">Checkout was cancelled. Select a plan to try again.</div>
                )}
                {!trialEligible && (
                    <div className="text-muted mt-2 small">
                        You've already used your free trial — but you can subscribe right now to get going.
                    </div>
                )}
            </div>

            {isPastDue && (
                <div className="text-center mb-4">
                    <button
                        className="btn btn-outline-primary"
                        onClick={handleManageBilling}
                        disabled={loading === 'portal'}
                    >
                        {loading === 'portal' ? <Spinner size="sm" /> : 'Update payment method'}
                    </button>
                </div>
            )}

            <div className="d-flex justify-content-center gap-4 flex-wrap" style={{ maxWidth: 960, margin: '0 auto' }}>
                {PLANS.map((plan) => {
                    // Filter out trial-mention features when the user isn't
                    // eligible — otherwise the card lies. `plan.trial` flag
                    // also gates the CTA copy below.
                    const trialOffered = !!plan.trial && trialEligible;
                    const features = plan.features.filter(f => trialOffered || !f.toLowerCase().includes('free trial'));
                    return (
                    <div
                        key={plan.tier}
                        className="d-flex flex-column"
                        style={{
                            width: 280,
                            background: '#fff',
                            borderRadius: 12,
                            border: plan.popular ? `2px solid ${plan.color}` : '1px solid #e5e7eb',
                            padding: 28,
                            position: 'relative',
                            boxShadow: plan.popular
                                ? '0 4px 24px rgba(38, 173, 169, 0.15)'
                                : '0 1px 3px rgba(0,0,0,0.06)',
                        }}
                    >
                        {plan.popular && (
                            <div
                                style={{
                                    position: 'absolute',
                                    top: -12,
                                    left: '50%',
                                    transform: 'translateX(-50%)',
                                    background: plan.color,
                                    color: '#fff',
                                    fontSize: '0.75rem',
                                    fontWeight: 600,
                                    padding: '2px 14px',
                                    borderRadius: 20,
                                }}
                            >
                                Most Popular
                            </div>
                        )}

                        <h3 className="fw-semibold mb-1" style={{ color: plan.color }}>{plan.name}</h3>
                        <div className="mb-3">
                            <span className="fw-bold" style={{ fontSize: '2rem' }}>£{plan.price}</span>
                            <span className="text-muted small">/month</span>
                        </div>

                        <div className="d-flex flex-column gap-2 mb-4 flex-grow-1">
                            {features.map((f) => (
                                <div key={f} className="d-flex align-items-start gap-2 small">
                                    <BiCheck size={18} className="flex-shrink-0 mt-0" style={{ color: plan.color }} />
                                    <span>{f}</span>
                                </div>
                            ))}
                        </div>

                        <button
                            className="btn w-100"
                            style={{
                                background: plan.popular ? plan.color : 'transparent',
                                color: plan.popular ? '#fff' : plan.color,
                                border: `1px solid ${plan.color}`,
                                fontWeight: 600,
                            }}
                            onClick={() => handleSubscribe(plan.tier)}
                            disabled={loading !== null}
                        >
                            {loading === plan.tier ? (
                                <Spinner size="sm" />
                            ) : trialOffered ? (
                                'Start free trial'
                            ) : (
                                'Subscribe'
                            )}
                        </button>
                    </div>
                    );
                })}
            </div>
        </div>
    );
}
